import { useCallback } from 'react'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { isSupportedChainId } from 'connectors/utils'
import { reportClientError } from 'utils/reportClientError'
import { useSetupNetwork } from './useSetupNetwork'

/** A wallet that has not answered in this long is not going to. */
const WALLET_ALIVE_TIMEOUT_MS = 8_000

/**
 * Ensures the wallet is on `requiredChainId` before a contract write (e.g. Base stake/reward proxy).
 */
export function useSwitchChainForContractTx() {
  const { chainId, provider } = useWeb3React()
  const { setupNetwork, awaitForNetworkSetup } = useSetupNetwork()

  /**
   * Does the wallet behind this page still answer?
   *
   * "Connected" is a claim the page makes from what it remembered: the address
   * is in storage, the balances render, the buttons enable — and a
   * WalletConnect session can be gone behind all of it, so every request
   * throws before reaching anyone. A holder with 40,215 MPRO waiting met that
   * five times on 6 September, was told "the request never reached the
   * network", and wrote to us that we had blocked them. Nothing was blocked.
   *
   * One cheap question, asked only when a write is about to happen, so a dead
   * session costs one refused button instead of a page that will not load.
   */
  const alive = useCallback(async (): Promise<boolean> => {
    const request = provider?.provider?.request
    if (typeof request !== 'function') return true // nothing to ask; let the send decide
    try {
      await Promise.race([
        provider!.provider!.request({ method: 'eth_chainId', params: [] }),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('wallet did not answer')), WALLET_ALIVE_TIMEOUT_MS)
        )
      ])
      return true
    } catch (error) {
      reportClientError({
        message: `wallet.sessionUnreachable: ${String((error as { message?: unknown })?.message ?? error).slice(
          0,
          200
        )}`,
        kind: 'wallet',
        handled: true
      })
      return false
    }
  }, [provider])

  return useCallback(
    async (requiredChainId: number): Promise<boolean> => {
      if (!requiredChainId || !isSupportedChainId(requiredChainId)) {
        return false
      }
      if (chainId === requiredChainId) {
        return alive()
      }
      const switched = await setupNetwork({ chainId: requiredChainId, modal: true, error: false })
      if (!switched) {
        return false
      }
      /*
       * The wait is bounded and its answer is used. Before, it looped forever
       * and the caller carried on regardless — so a wallet that never switched
       * produced a contract call on the wrong chain, which the user met as an
       * unexplained failure.
       */
      const landed = await awaitForNetworkSetup(requiredChainId)
      if (!landed) return false
      await new Promise((resolve) => setTimeout(resolve, 300))
      return true
    },
    [alive, chainId, setupNetwork, awaitForNetworkSetup]
  )
}
